import React, { useState, useEffect, useMemo } from 'react';
import { useAuthorizer, useAuthorizerContext } from './AuthorizerContext';
import { AuthGateInteractor, AuthGateViewState } from './AuthGateInteractor';
import { EditPermissionDenied } from './EditPermissionDenied';
import { LoginModal } from './LoginModal';
import { useNavigate, useLocation } from 'react-router-dom';

interface AuthGateProps {
  children: React.ReactNode;
  isDevelopment: boolean;
}

export const AuthGate: React.FC<AuthGateProps> = ({ children, isDevelopment }) => {
  const authorizer = useAuthorizer();
  const { authState } = useAuthorizerContext();
  const [viewState, setViewState] = useState<AuthGateViewState>({
    status: 'loading',
    isLoading: true,
    isCheckingEntitlement: false,
    error: null,
    treeId: null
  });
  const [showLoginModal, setShowLoginModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [hasHandledRouting, setHasHandledRouting] = useState(false);

  // Create AuthGateInteractor instance (only once, reuse it)
  const interactor = useMemo(() => {
    return new AuthGateInteractor(authorizer, isDevelopment, location.pathname);
  }, [authorizer, isDevelopment]);

  // Set up callback to update view state
  useEffect(() => {
    interactor.setUpdateCallback(setViewState);
  }, [interactor]);

  // Update pathname when it changes
  useEffect(() => {
    interactor.updatePathname(location.pathname);
  }, [interactor, location.pathname]);

  // Initialize interactor when auth state changes
  useEffect(() => {
    interactor.initialize();
  }, [interactor, authState.user]);

  // Handle routing after auth is initialized
  useEffect(() => {
    if (viewState.status !== 'loading' && !hasHandledRouting) {
      // Only redirect from root path
      if (location.pathname === '/' || location.pathname === '') {
        navigate('/tree/tekken-8', { replace: true });
        setHasHandledRouting(true);
        return;
      }
      setHasHandledRouting(true);
    }
  }, [viewState.status, hasHandledRouting, navigate, location.pathname]);

  // Show login modal when mustLogin status
  useEffect(() => {
    if (viewState.status === 'mustLogin') {
      setShowLoginModal(true);
    }
  }, [viewState.status]);

  // Render based on view state
  // Only show loading screen during initial load, not during entitlement re-checks
  // This prevents children from unmounting when token refreshes trigger re-initialization
  if (viewState.status === 'loading' && !viewState.isCheckingEntitlement) {
    return (
      <div className="loading-container">
        <h1 className="loading-spinner">Loading Skill Trees...</h1>
      </div>
    );
  }

  if (viewState.status === 'mustLogin') {
    return (
      <>
        <LoginModal 
          isOpen={showLoginModal} 
          onClose={() => setShowLoginModal(false)} 
        />
      </>
    );
  }

  if (viewState.status === 'needsPermission') {
    return <EditPermissionDenied treeId={viewState.treeId} />;
  }

  // status === 'authorized'
  return <>{children}</>;
};